import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, MapPin, Package, Search } from "lucide-react";
import { rpc } from "../../lib/rpc";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const PAGE_SIZE = 30;

/**
 * Floor-Mode-Artikelliste (#114, Wunsch Hauptnutzer 2026-08-23).
 *
 * Read-only: suchen, nach Kategorie filtern, blättern. Je Artikel die
 * Lagerorte (Raum · Regal · Fach) mit Menge, damit man weiß, wo man
 * etwas findet. Die Master-Listen sind für alle angemeldeten Rollen lesbar.
 */
export default function MobileArtikel() {
    const [articles, setArticles] = useState(null);
    const [categories, setCategories] = useState([]);
    const [positions, setPositions] = useState([]);
    const [compartments, setCompartments] = useState([]);
    const [shelves, setShelves] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("all");
    const [onlyInStock, setOnlyInStock] = useState(false);
    const [page, setPage] = useState(0);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        Promise.all([
            rpc.get("/master/articles"),
            rpc.get("/master/categories"),
            rpc.get("/master/stock-positions"),
            rpc.get("/master/compartments"),
            rpc.get("/master/shelves"),
            rpc.get("/master/rooms"),
        ]).then(([a, c, p, k, sh, r]) => {
            if (!a.ok) {
                setError(a.error || "Artikel konnten nicht geladen werden.");
                setArticles([]);
                return;
            }
            setArticles(a.data.filter((x) => x.lifecycle !== "archived"));
            if (c.ok) setCategories(c.data);
            if (p.ok) setPositions(p.data);
            if (k.ok) setCompartments(k.data);
            if (sh.ok) setShelves(sh.data);
            if (r.ok) setRooms(r.data);
        });
    }, []);

    const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);

    // article_id -> [{ qty, place }], place = "Raum · Regal · Fach-Code"
    const placesByArticle = useMemo(() => {
        const compMap = Object.fromEntries(compartments.map((c) => [c.id, c]));
        const shelfMap = Object.fromEntries(shelves.map((s) => [s.id, s]));
        const roomMap = Object.fromEntries(rooms.map((r) => [r.id, r]));
        const m = {};
        for (const p of positions) {
            const comp = compMap[p.compartment_id];
            const shelf = comp ? shelfMap[comp.shelf_id] : null;
            const room = shelf ? roomMap[shelf.room_id] : null;
            const place = [room?.name, shelf?.name, comp?.code || comp?.name]
                .filter(Boolean)
                .join(" · ");
            (m[p.article_id] = m[p.article_id] || []).push({
                id: p.id,
                qty: p.quantity || 0,
                place: place || p.compartment_id?.slice(0, 8) || "—",
            });
        }
        return m;
    }, [positions, compartments, shelves, rooms]);

    const totalQty = useCallback(
        (id) => (placesByArticle[id] || []).reduce((s, x) => s + x.qty, 0),
        [placesByArticle]
    );

    const filtered = useMemo(() => {
        if (!articles) return [];
        const s = search.trim().toLowerCase();
        return articles
            .filter((a) => {
                if (category !== "all" && a.category_id !== category) return false;
                if (onlyInStock && totalQty(a.id) <= 0) return false;
                if (!s) return true;
                const hay = [
                    a.name,
                    a.ean,
                    a.manufacturer_number,
                    catMap[a.category_id]?.name,
                    ...(placesByArticle[a.id] || []).map((x) => x.place),
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return hay.includes(s);
            })
            .sort((a, b) => (a.name || "").localeCompare(b.name || "", "de"));
    }, [articles, search, category, onlyInStock, catMap, placesByArticle, totalQty]);

    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount - 1);
    const pageRows = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

    return (
        <section className="flex flex-col gap-4" data-testid="artikel-page">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Floor Mode
                </div>
                <h1 className="text-3xl font-bold tracking-tight text-foreground">Artikel</h1>
                <p className="mt-1 text-xs text-muted-foreground">
                    Wo liegt was? Suchen, filtern, Lagerort je Artikel aufklappen.
                </p>
            </div>

            <div className="flex flex-col gap-2">
                <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Name, EAN, Hersteller-Nr., Lagerort …"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                        className="h-11 pl-9"
                        aria-label="Artikel suchen"
                        data-testid="artikel-search"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Select
                        value={category}
                        onValueChange={(v) => {
                            setCategory(v);
                            setPage(0);
                        }}
                    >
                        <SelectTrigger className="h-10 flex-1" aria-label="Kategorie" data-testid="artikel-filter-category">
                            <SelectValue placeholder="Kategorie" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">alle Kategorien</SelectItem>
                            {categories.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                    {c.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <label className="flex h-10 items-center gap-2 rounded-md border border-border px-3 text-xs text-muted-foreground">
                        <input
                            type="checkbox"
                            checked={onlyInStock}
                            onChange={(e) => {
                                setOnlyInStock(e.target.checked);
                                setPage(0);
                            }}
                            className="h-4 w-4"
                            data-testid="artikel-filter-in-stock"
                        />
                        nur mit Bestand
                    </label>
                </div>
            </div>

            {articles === null ? (
                <div className="text-sm text-muted-foreground">Lade …</div>
            ) : error ? (
                <div
                    className="rounded-xl border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive"
                    data-testid="artikel-error"
                >
                    {error}
                </div>
            ) : filtered.length === 0 ? (
                <div
                    className="rounded-xl border border-border bg-card/40 p-6 text-center text-sm text-muted-foreground"
                    data-testid="artikel-empty"
                >
                    Keine Artikel gefunden.
                </div>
            ) : (
                <>
                    <div className="text-xs text-muted-foreground" data-testid="artikel-count">
                        {filtered.length} von {articles.length} Artikeln
                    </div>
                    <ul className="flex flex-col gap-2" data-testid="artikel-liste">
                        {pageRows.map((a) => {
                            const places = placesByArticle[a.id] || [];
                            const open = expanded === a.id;
                            const qty = totalQty(a.id);
                            return (
                                <li
                                    key={a.id}
                                    className="rounded-lg border border-border bg-card/40"
                                    data-testid="artikel-item"
                                >
                                    <button
                                        type="button"
                                        onClick={() => setExpanded(open ? null : a.id)}
                                        aria-expanded={open}
                                        className="pw-tap flex w-full items-center gap-3 px-3 py-3 text-left"
                                        data-testid="artikel-item-toggle"
                                    >
                                        <Package className="h-5 w-5 shrink-0 text-muted-foreground" />
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-sm font-medium text-foreground">
                                                {a.name}
                                            </span>
                                            <span className="block truncate text-xs text-muted-foreground">
                                                {[
                                                    catMap[a.category_id]?.name,
                                                    a.manufacturer_number ? `Hersteller-Nr. ${a.manufacturer_number}` : null,
                                                    a.ean ? `EAN ${a.ean}` : null,
                                                ]
                                                    .filter(Boolean)
                                                    .join(" · ") || "—"}
                                            </span>
                                        </span>
                                        <span
                                            className={`shrink-0 rounded-full px-2 py-0.5 font-mono text-xs ${
                                                qty > 0
                                                    ? "bg-primary/10 text-primary"
                                                    : "bg-muted text-muted-foreground"
                                            }`}
                                            data-testid="artikel-item-qty"
                                        >
                                            {qty}
                                        </span>
                                        {open ? (
                                            <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
                                        ) : (
                                            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                                        )}
                                    </button>
                                    {open ? (
                                        <div
                                            className="border-t border-border/60 px-3 py-2"
                                            data-testid="artikel-item-places"
                                        >
                                            {places.length === 0 ? (
                                                <div className="text-xs text-muted-foreground">
                                                    Kein Lagerplatz zugeordnet.
                                                </div>
                                            ) : (
                                                <ul className="flex flex-col gap-1">
                                                    {places.map((p) => (
                                                        <li
                                                            key={p.id}
                                                            className="flex items-start gap-2 text-xs"
                                                        >
                                                            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                                                            <span className="flex-1 break-words">{p.place}</span>
                                                            <span className="font-mono">{p.qty}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                    {pageCount > 1 ? (
                        <div className="flex items-center justify-between text-xs" data-testid="artikel-pager">
                            <button
                                type="button"
                                className="pw-tap rounded-md border border-border px-3 py-2 disabled:opacity-40"
                                disabled={safePage === 0}
                                onClick={() => setPage(safePage - 1)}
                                data-testid="artikel-pager-prev"
                            >
                                Zurück
                            </button>
                            <span className="text-muted-foreground">
                                Seite {safePage + 1} / {pageCount}
                            </span>
                            <button
                                type="button"
                                className="pw-tap rounded-md border border-border px-3 py-2 disabled:opacity-40"
                                disabled={safePage >= pageCount - 1}
                                onClick={() => setPage(safePage + 1)}
                                data-testid="artikel-pager-next"
                            >
                                Weiter
                            </button>
                        </div>
                    ) : null}
                </>
            )}
        </section>
    );
}
